import { Dialog, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CircleXButton } from 'src/components/Button/CircleXButton';
import { useProductsListQuery } from 'src/providers/useProductsListQuery';
import {
  CancelButton,
  FileDetailsDialogContent,
  FileDetailsDialogTitle,
  FilesListContainer,
  FlexRow,
  HeadersContainer,
  Label,
  LeftColumn,
  RightColumn,
  StyledDialogActions,
  StyledDivider,
} from '../styles';
import { ProductFile } from '../Uploader';
import { FileDetailsRow } from './FileDetailsRow';
import { FileDetailsSaveButton } from './FileDetailsSaveButton';

type FileDetailsUploadModalProps = Readonly<{
  isOpen: boolean;
  productFiles: ProductFile[];
  setProductFiles: React.Dispatch<React.SetStateAction<ProductFile[]>>;
  uploadFiles: () => void;
  onClose: () => void;
}>;

export function FileDetailsUploadModal({
  isOpen,
  onClose,
  productFiles,
  setProductFiles,
  uploadFiles,
}: FileDetailsUploadModalProps) {
  const { data: products = [] } = useProductsListQuery();
  const [disabled, setDisabled] = React.useState(false);

  const { t } = useTranslation('uploads');

  /* istanbul ignore next */
  const editFileDetailsFactory = (file: ProductFile) => (newDetails: Partial<ProductFile>) => {
    setProductFiles((prevFiles) =>
      prevFiles.map((prevFile) => {
        if (prevFile.name === file.name) {
          return { ...prevFile, ...newDetails };
        }
        return prevFile;
      })
    );
  };

  const removeFileFactory = (file: ProductFile) => () => {
    setProductFiles((prevFiles) => {
      const updatedFiles = prevFiles.filter((prevFile) => prevFile.name !== file.name);
      if (updatedFiles.length === 0) {
        onClose();
      }
      return updatedFiles;
    });
  };

  const filesToUpload = productFiles.filter((file) => file.uploadState === 'initial');

  return (
    <Dialog
      fullWidth
      PaperProps={{ style: { overflow: 'visible', maxWidth: 749 } }}
      open={isOpen}
      data-testid="file-upload-details-modal"
    >
      <CircleXButton onClick={onClose} />
      <FileDetailsDialogTitle>{t('uploadFiles')}</FileDetailsDialogTitle>
      <FileDetailsDialogContent>
        <Typography>
          {`${t('assignFileToProduct', { count: productFiles.length })}. ${t('youCanProvideADescriptionForEachFile')}`}
        </Typography>
        <FilesListContainer>
          <HeadersContainer>
            <FlexRow>
              <LeftColumn>
                <Label>{t('file')}</Label>
              </LeftColumn>
              <RightColumn>
                <FlexRow sx={{ justifyContent: 'flex-start' }}>
                  <Label>{t('assignToProduct')}</Label>
                </FlexRow>
              </RightColumn>
            </FlexRow>
            <StyledDivider />
          </HeadersContainer>
          {filesToUpload.map((productFile) => (
            <FileDetailsRow
              key={productFile.name}
              productFile={productFile}
              editFileDetails={editFileDetailsFactory(productFile)}
              removeFile={removeFileFactory(productFile)}
              products={products}
              setDisabled={setDisabled}
            />
          ))}
        </FilesListContainer>
      </FileDetailsDialogContent>
      <StyledDialogActions>
        <CancelButton onClick={onClose} data-testid="cancel-details-button">
          {t('discardChanges')}
        </CancelButton>
        <FileDetailsSaveButton data-testId="file-details-save-button" uploadFiles={uploadFiles} disabled={disabled} />
      </StyledDialogActions>
    </Dialog>
  );
}
